"use client";

import React, { FC } from "react";
import { useAppSelector } from "@/store/hook";
import GenerateUID from "@/utils/generateUID";
import { selectCar, selectCarLoadingStates } from "@/store/selectors/data/CarSelectors";
import {
  selectOffersSelected,
  selectOffersSelectedLoadingStates,
} from "@/store/selectors/jsonld/OfferSelector";
import Image from "next/image";
import { IoCarSport } from "react-icons/io5";
import Chip from "@/components/uix/Chip/Chip";
import ContactFormContainer from "@/components/uix/forms/Contact/ContactFormContainer";
import { TypeOffer } from "@/types/jsonld/jsonld.types";
import PriceDisplay from "@/components/uix/PriceDisplay/PriceDisplay";

interface TPageProps {}

const Page: FC<TPageProps> = (props: TPageProps) => {
  const car = useAppSelector(selectCar);
  const carLoading = useAppSelector(selectCarLoadingStates);
  const offersSelected = useAppSelector(selectOffersSelected);
  const offerSelectedLoading = useAppSelector(selectOffersSelectedLoadingStates);

  // Fonction pour récupérer et calculer le prix total en tenant compte des offres liées
  const getOfferDetails = (
    offers: TypeOffer[],
  ): { offerDetails: string; totalAmount: number; priceCurrency: string } => {
    let totalAmount = 0;
    let offerDetails = "Offres sélectionnées :\n";
    let priceCurrency = "";

    const offerReprogrammation = offers.find((offer) => offer.type === "REPROGRAMMATION");
    const offersCarburant = offers.filter((offer) => offer.type === "OFFER");

    if (offerReprogrammation) {
      // Ajoute le prix et le nom de la reprogrammation
      totalAmount += offerReprogrammation.amount;
      offerDetails += `- ${offerReprogrammation.thing?.name}: ${offerReprogrammation.amount} ${offerReprogrammation.priceCurrency}\n`;
      priceCurrency = offerReprogrammation.priceCurrency; // Récupère la devise de la reprogrammation
    }

    // Traiter toutes les offres carburant, même sans reprogrammation
    offersCarburant.forEach((offer) => {
      // Cherche si l'offre est liée à la reprogrammation pour ajuster le prix
      const linkedOfferPrice = offer.linkedOffers?.find(
        (linkedOffer) =>
          offerReprogrammation && linkedOffer.linkedOfferId === offerReprogrammation.id,
      )?.amount;

      const offerPrice = linkedOfferPrice ?? offer.amount;
      totalAmount += offerPrice;

      // Ajoute le nom et le prix de chaque offre carburant
      offerDetails += `- ${offer.thing?.name}: ${offerPrice} ${offer.priceCurrency}\n`;
      priceCurrency = offer.priceCurrency; // Met à jour la devise pour la dernière offre
    });

    // Si aucune offre de reprogrammation n'est trouvée, mais qu'il y a des offres carburant, afficher le total
    if (totalAmount === 0 && offersCarburant.length > 0) {
      totalAmount = offersCarburant.reduce((sum, offer) => sum + offer.amount, 0);
      priceCurrency = offersCarburant[0].priceCurrency; // Récupère la devise de la première offre carburant
    }

    // Ajoute le total à l'affichage
    offerDetails += `Total: ${totalAmount} ${priceCurrency}\n`;

    return { offerDetails, totalAmount, priceCurrency };
  };

  const CarRender = () => {
    return (
      <section>
        {car && (
          <div>
            <div className='bg-gradient-to-tl from-primary/15 py-4'>
              <h1 className='text-2xl text-center'>{car.version?.model.brand.name}</h1>
              <div className='flex justify-center'>
                {car.version?.model.brand.img && (
                  <Image
                    src={`/data/image/${car.version?.model.brand.img}`}
                    width={80}
                    height={80}
                    alt={car.version?.model.brand.name}
                    className='flex gap-2 w-20 h-auto'
                  />
                )}
              </div>
            </div>
            <div className='max-w-4xl mx-auto py-1'>
              <div className='my-1 p-2 border rounded'>
                <div className='bg-gradient-to-r from-primary/5 text-primary text-2xl italic py-1 px-2'>
                  Votre modèle de véhicule
                </div>

                <div className='bg-gradient-to-l from-primary/5 text-2xl italic font-bold p-2'>
                  <div className='flex gap-2 align-middle justify-between'>
                    <div className='flex gap-2 align-middle justify-start'>
                      <IoCarSport className='w-8 h-8 text-blue-500' />
                      <div className='flex flex-col text-md'>
                        <span className='text-blue-400'>{car.version?.model.name}</span>
                        <span className='text-sm'>{car.version?.name}</span>
                        <span className='text-sm'>
                          {car.name} - {car.fuel}
                        </span>
                      </div>
                    </div>
                    <div>{car.chips && <Chip chip={car.chips} />}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  };

  const OffersRender = () => {
    const offers = offersSelected || [];

    const { offerDetails, totalAmount, priceCurrency } = getOfferDetails(offers);

    return (
      <section className='space-y-1 px-1 md:px-4'>
        {offersSelected && offersSelected.length > 0 && (
          <div className='space-y-2'>
            <span className='text-center italic text-primary'>
              Vous nous contactez pour plus d&rsquo;informations sur les offres:
            </span>
            <div className='space-y-1'>
              {offersSelected.map((offer) => {
                return (
                  <div
                    key={`Offer-${GenerateUID()}`}
                    className='bg-gradient-to-tr from-primary/10 rounded-md border border-1 border-dashed border-primary/50 p-2'
                  >
                    <div className='flex items-center justify-between'>
                      <span>{offer.thing?.name}</span>
                      <span className='font-bold text-secondary/80'>
                        <PriceDisplay amount={offer.amount} currency={offer?.priceCurrency} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className='bg-gradient-to-l from-primary/15 text-end text-lg font-bold p-1'>
              <strong>
                <span>Total: </span>
                <span className='text-secondary'>
                  <PriceDisplay amount={totalAmount} currency={priceCurrency} />
                </span>
              </strong>
            </div>
          </div>
        )}
      </section>
    );
  };

  return (
    <main className='space-y-1 px-1 md:px-4'>
      <section>
        {!carLoading.isIdle && !carLoading.isFailed && !carLoading.isSucceeded ? (
          <span className='flex justify-center'>
            Chargement de votre véhicule, veuillez patienter.
          </span>
        ) : (
          <CarRender />
        )}
      </section>
      <section className='max-w-4xl mx-auto'>
        {!offerSelectedLoading.isIdle &&
        !offerSelectedLoading.isSucceeded &&
        !carLoading.isSucceeded ? (
          <span className='flex justify-center'>
            Nous chargeons les offres qui vous intéressent.
          </span>
        ) : (
          <OffersRender />
        )}
      </section>
      <section className='max-w-4xl mx-auto mt-2'>
        <div>
          <h1 className='bg-gradient-to-b from-primary/25 py-1 px-2 italic'>
            Contact RSTronic Martinique
          </h1>
          <div className='bg-gradient-to-br from-primary/10 border border-1 rounded mt-1 py-2 px-5'>
            <ContactFormContainer />
          </div>
        </div>
      </section>
    </main>
  );
};

export default Page;
